import { useState, useCallback, useMemo } from 'react';
import { Calendar, Users, Trophy, TrendingUp } from 'lucide-react';
import useRailFilters from './useRailFilters';
import { CONFERENCE_MAP } from '../../../../pages/sport-dashboards/ncaaf/ncaafConfig';

/**
 * useNCAAFRailFilters - NCAAF-specific rail filter configuration
 * Provides tabs, filters, and handlers for NCAAF dashboard
 * 
 * @param {Function} onFiltersChange - Callback when filters change
 * @param {Date} initialDate - Initial date value
 * @param {Object} initialConference - Initial conference value
 * @param {Object} initialTeam - Initial team value
 */
const useNCAAFRailFilters = ({ 
  onFiltersChange, 
  initialDate,
  initialConference = null,
  initialTeam = null
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [showConferenceSheet, setShowConferenceSheet] = useState(false);
  const [showTeamSheet, setShowTeamSheet] = useState(false);
  
  // Default filter values - captured once on mount, never change
  const defaultFilters = useMemo(() => {
    // For NCAAF, always default to nearest Saturday regardless of initialDate
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Calculate days until Saturday
    let daysUntilSaturday;
    if (dayOfWeek === 6) {
      // If it's Saturday, use today
      daysUntilSaturday = 0;
    } else if (dayOfWeek === 0) {
      // If it's Sunday, go to next Saturday (6 days)
      daysUntilSaturday = 6;
    } else {
      // Monday through Friday: go to upcoming Saturday
      daysUntilSaturday = 6 - dayOfWeek;
    }
    
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysUntilSaturday);
    
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return {
      date: dateStr,
      conference: initialConference,
      team: initialTeam,
      division: 'all'
    };
  }, []); // Empty deps - capture initial values only once!
  
  // Use base rail filters hook
  const {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    buildFilterObjects
  } = useRailFilters({
    sport: 'ncaaf',
    defaultFilters,
    onFiltersChange
  });
  
  // Format date for display
  const formatDateLabel = useCallback((dateStr) => {
    if (!dateStr) return 'Select Date';
    
    // Handle if dateStr is actually a Date object
    if (dateStr instanceof Date) {
      const year = dateStr.getFullYear();
      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
      const day = String(dateStr.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    
    // Validate the date string format
    if (typeof dateStr !== 'string' || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return 'Select Date';
    }
    
    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);
    
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    
    // Compare dates properly
    const dateCompare = new Date(date);
    dateCompare.setHours(12, 0, 0, 0);
    
    if (dateCompare.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateCompare.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[date.getDay()];
      const monthNum = date.getMonth() + 1;
      const dayNum = date.getDate();
      return `${dayName} ${monthNum}/${dayNum}`;
    }
  }, []);
  
  // Format conference for display
  const formatConferenceLabel = useCallback((conference) => {
    if (!conference) return 'All Conferences';
    if (typeof conference === 'object') return conference.name || conference.id;
    return conference;
  }, []);
  
  // Format team for display
  const formatTeamLabel = useCallback((team) => {
    if (!team) return 'All Teams';
    if (typeof team === 'object') return team.name || team.id;
    return team;
  }, []);
  
  // Primary tabs configuration - NCAAF conferences
  const primaryTabs = useMemo(() => {
    return [
      { id: 'all', label: 'All Games' },
      { id: 'top25', label: 'Top 25' },
      { id: 'sec', label: 'SEC' },
      { id: 'big10', label: 'Big Ten' },
      { id: 'acc', label: 'ACC' },
      { id: 'big12', label: 'Big 12' },
      { id: 'pac12', label: 'Pac-12' },
      { id: 'browse', label: 'Browse...'}
    ];
  }, []);
  
  // Secondary filter configuration
  const filterConfig = useMemo(() => {
    const configs = [
      {
        id: 'date',
        type: 'date',
        getLabel: formatDateLabel,
        hasDropdown: true
      }
    ];
    
    // Only show conference chip if "All Games" is selected
    if (activeTab === 'all') {
      configs.push({
        id: 'conference',
        type: 'select',
        getLabel: formatConferenceLabel,
        hasDropdown: true
      });
    }
    
    // Add team chip
    configs.push({
      id: 'team',
      type: 'select',
      getLabel: formatTeamLabel,
      hasDropdown: true
    });
    
    return configs;
  }, [activeTab, formatDateLabel, formatConferenceLabel, formatTeamLabel]);
  
  // Build filter objects for SecondaryRail
  const filterObjects = buildFilterObjects(filterConfig);
  
  // Handle filter chip click
  const handleFilterClick = useCallback((filterId, filterData) => {
    switch (filterId) {
      case 'date':
        setShowDateSheet(true);
        break;
      case 'conference':
        setShowConferenceSheet(true);
        break;
      case 'team':
        setShowTeamSheet(true);
        break;
      default:
        break;
    }
  }, []);
  
  // Handle primary tab change
  const handleTabChange = useCallback((tabId) => {
    if (tabId === 'browse') {
      setShowConferenceSheet(true);
      return;
    }
    
    setActiveTab(tabId);
    
    // Update conference filter based on tab selection
    if (tabId === 'top25') {
      updateFilter('division', 'top25');
      updateFilter('conference', null);
    } else if (tabId !== 'all') {
      // Use conference mapping from config
      updateFilter('conference', CONFERENCE_MAP[tabId] || tabId);
      updateFilter('division', tabId);
    } else {
      updateFilter('conference', null);
      updateFilter('division', 'all');
    }
  }, [updateFilter]);
  
  return {
    // State
    filters,
    activeTab,
    hasActiveFilters,
    
    // Tabs and filters
    primaryTabs,
    filterObjects,
    
    // Handlers
    handleTabChange,
    handleFilterClick,
    updateFilter,
    resetFilters,
    
    // Sheet visibility
    showDateSheet,
    setShowDateSheet,
    showConferenceSheet,
    setShowConferenceSheet,
    showTeamSheet,
    setShowTeamSheet
  };
};

export default useNCAAFRailFilters;